// LOS IMPORTES DE UNA VENTA, CALCULADOS COMO LOS CALCULA EL SERVIDOR.
// DIRECTRIZ-PWA F2, corrección P1-1 de AUDITORIA-F2 (11 oct 2026).
//
// POR QUÉ EXISTE. Sin señal, el teléfono imprime el ticket con lo que él
// calcula; con señal, lo que manda es lo que registra `POST /ventas`. Si las
// dos cuentas difieren en un centavo, el cliente se lleva un papel que no
// cuadra con el corte. Pasó: `3 × $33.30 con 15 %` daba $84.92 en el
// teléfono y $84.91 en el servidor, porque el teléfono redondeaba la SUMA de
// los descuentos y el servidor redondea CADA uno.
//
// EL MISMO ORDEN Y EL MISMO REDONDEO que `routers/ventas.py` (`_r2`, media
// hacia arriba) y `fiscal.desglosar`, paso a paso:
//   importe_bruto = r2(precio × cantidad)                  por renglón
//   descuento     = r2(importe_bruto × valor / 100) | r2(monto)
//   importe       = r2(importe_bruto − descuento)
//   base_global   = r2(Σ bruto − Σ descuentos de renglón)
//   global        = r2(base_global × valor / 100) | r2(monto)
//   descuento_total = r2(Σ descuentos de renglón + global)
//   cobrado       = r2(Σ bruto − descuento_total)
//   precios con impuesto: subtotal = r2(cobrado / (1 + tasa)), impuestos = total − subtotal
//   precios sin impuesto: impuestos = r2(cobrado × tasa), total = subtotal + impuestos
//
// ARITMÉTICA EXACTA. Todo va en enteros (`BigInt`) con su escala decimal:
// ningún `float` se acumula, así que `0.1 + 0.2` no existe aquí. Lo que sale
// son números en pesos con dos decimales exactos (centavos / 100).
//
// UNA TASA POR PRODUCTO (S-1 de la segunda vuelta de AUDITORIA-F2). Cada
// renglón puede traer `trato_iva` (GENERAL, TASA_0 o EXENTO), tal como viaja
// en la copia del catálogo. Como `fiscal.resolver` + `fiscal.tasa_de`: TASA_0
// y EXENTO valen 0 %; GENERAL (o sin trato) vale la tasa de la empresa. Lo
// cobrado se agrupa por tasa, el descuento sobre el total se reparte entre
// las tasas en proporción (`fiscal.repartir_descuento`, el último centavo al
// grupo más grande) y cada grupo se desglosa aparte (`fiscal.desglosar`).
// Con una sola tasa es exactamente la cuenta de siempre.
//
// Si el teléfono NO sabe cómo se grava un producto, no adivina: quien cobra
// sin señal lo comprueba antes con `faltaTratoFiscal` y no deja cobrar.

const DIEZ = 10n;

/** Un decimal exacto: valor = n / 10^s. Acepta número o texto ('33.30', 3, '0.16'). */
export function decimal(v) {
  if (v === null || v === undefined || v === '') return { n: 0n, s: 0 };
  let t = typeof v === 'number' ? String(v) : String(v).trim();
  if (/e/i.test(t)) t = Number(t).toFixed(12);
  const m = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(t);
  if (!m) return { n: 0n, s: 0 };
  const entero = m[2] || '0';
  const frac = m[3] || '';
  const n = BigInt(entero + frac) * (m[1] === '-' ? -1n : 1n);
  return { n, s: frac.length };
}

const pot = (s) => DIEZ ** BigInt(s);

function mul(a, b) { return { n: a.n * b.n, s: a.s + b.s }; }

/** División entera redondeada media hacia arriba (alejándose de cero), como ROUND_HALF_UP. */
function dividirRedondeando(num, den) {
  if (den < 0n) { num = -num; den = -den; }
  const neg = num < 0n;
  const a = neg ? -num : num;
  let q = a / den;
  const r = a % den;
  if (r * 2n >= den) q += 1n;
  return neg ? -q : q;
}

/** A centavos (BigInt), media hacia arriba: el `_r2` del servidor. */
export function aCentavos(x) {
  const d = typeof x === 'object' && x !== null && 'n' in x ? x : decimal(x);
  if (d.s <= 2) return d.n * pot(2 - d.s);
  return dividirRedondeando(d.n, pot(d.s - 2));
}

const pesos = (c) => Number(c) / 100;

function descuentoEnCentavos(d, baseCentavos) {
  if (!d) return 0n;
  if (d.tipo === 'PORCENTAJE') {
    const v = decimal(d.valor);
    // base (centavos) × valor / 100 → centavos, redondeado una vez.
    return dividirRedondeando(baseCentavos * v.n, 100n * pot(v.s));
  }
  return aCentavos(d.valor);
}

/** Los tratos que el servidor conoce (`fiscal.TRATOS`). */
export const TRATOS_IVA = ['GENERAL', 'TASA_0', 'EXENTO'];

/** `fiscal.tasa_de`: TASA_0 y EXENTO valen cero; lo demás, la tasa de la empresa. */
function tasaDeTrato(trato, tasaEmpresa) {
  return trato === 'TASA_0' || trato === 'EXENTO' ? { n: 0n, s: 0 } : tasaEmpresa;
}

/** La misma tasa escrita de dos formas ('0.16', '0.160') es UNA clave, como en `Decimal`. */
function claveDeTasa({ n, s }) {
  let a = n; let e = s;
  while (e > 0 && a % DIEZ === 0n) { a /= DIEZ; e -= 1; }
  return `${a}e-${e}`;
}

/**
 * `fiscal.repartir_descuento`: el descuento global, en proporción a lo bruto
 * de cada tasa, redondeado por grupo; lo que sobra o falta va al grupo más
 * grande (el primero, si empatan).
 */
function repartirDescuento(grupos, desc) {
  for (const g of grupos.values()) g.reparto = 0n;
  if (desc <= 0n || grupos.size === 0) return;
  let totalBruto = 0n;
  for (const g of grupos.values()) totalBruto += g.bruto;
  if (totalBruto <= 0n) return;
  let suma = 0n;
  let mayor = null;
  for (const g of grupos.values()) {
    g.reparto = dividirRedondeando(desc * g.bruto, totalBruto);
    suma += g.reparto;
    if (mayor === null || g.bruto > mayor.bruto) mayor = g;
  }
  mayor.reparto += desc - suma;
}

/**
 * ¿Algún renglón de esta venta no dice cómo se grava? Devuelve el primero
 * que falta (o null). Sin señal, una venta así no se cobra: el ticket no
 * puede saber cuánto IVA lleva.
 */
export function faltaTratoFiscal(renglones = []) {
  return renglones.find((r) => !TRATOS_IVA.includes(r.trato_iva)) || null;
}

/**
 * Los importes de una venta.
 *   renglones: [{precio_unitario, cantidad, descuento?, trato_iva?}]
 *   descuento: el descuento sobre el total (o null)
 *   config: {tasa_impuesto, precios_incluyen_impuesto}
 * Devuelve pesos con dos decimales exactos:
 *   {renglones: [{importe_bruto, descuento, importe}], bruto,
 *    descuento_renglones, descuento_global, descuento_total, cobrado,
 *    subtotal, impuestos, total}
 */
export function importesDeVenta(renglones = [], descuento = null, config = {}) {
  let bruto = 0n;
  let descR = 0n;
  const brutos = [];
  const descuentos = [];
  // S-3: un descuento mayor que su importe no se cobra (el servidor lo
  // rechaza con 400). Aquí no baja de cero, y `excedido` dice cuál fue para
  // que la caja no deje cobrar y lo explique.
  let excedido = null;
  const filas = renglones.map((r, i) => {
    const ib = aCentavos(mul(decimal(r.precio_unitario), decimal(r.cantidad)));
    let dc = descuentoEnCentavos(r.descuento, ib);
    if (dc > ib) {
      if (!excedido) excedido = { renglon: i, descuento: pesos(dc), importe: pesos(ib) };
      dc = ib;
    }
    bruto += ib;
    descR += dc;
    brutos.push(ib);
    descuentos.push(dc);
    return { importe_bruto: pesos(ib), descuento: pesos(dc), importe: pesos(ib - dc) };
  });
  const baseGlobal = bruto - descR;
  let descG = descuento ? descuentoEnCentavos(descuento, baseGlobal) : 0n;
  if (descG > baseGlobal) {
    if (!excedido) excedido = { renglon: 'TOTAL', descuento: pesos(descG), importe: pesos(baseGlobal) };
    descG = baseGlobal;
  }
  const descTotal = descR + descG;
  const cobrado = bruto - descTotal;

  const tasaEmpresa = decimal(config?.tasa_impuesto ?? 0);
  // Por tasa, en el orden en que aparecen (como el `dict` del servidor).
  const grupos = new Map();
  renglones.forEach((r, i) => {
    const t = tasaDeTrato(r.trato_iva, tasaEmpresa);
    const k = claveDeTasa(t);
    const g = grupos.get(k) || { tasa: t, bruto: 0n, desc: 0n };
    g.bruto += brutos[i];
    g.desc += descuentos[i];
    grupos.set(k, g);
  });
  repartirDescuento(grupos, descG);

  const incluyen = config?.precios_incluyen_impuesto !== false;
  let subtotal = 0n; let impuestos = 0n; let total = 0n;
  for (const g of grupos.values()) {
    const cob = g.bruto - g.desc - g.reparto;
    const { n, s: e } = g.tasa;
    if (incluyen) {
      const sub = n === 0n ? cob : dividirRedondeando(cob * pot(e), pot(e) + n);
      subtotal += sub; impuestos += cob - sub; total += cob;
    } else {
      const imp = dividirRedondeando(cob * n, pot(e));
      subtotal += cob; impuestos += imp; total += cob + imp;
    }
  }
  return {
    renglones: filas,
    bruto: pesos(bruto),
    descuento_renglones: pesos(descR),
    descuento_global: pesos(descG),
    descuento_total: pesos(descTotal),
    cobrado: pesos(cobrado),
    subtotal: pesos(subtotal),
    impuestos: pesos(impuestos),
    total: pesos(total),
    excedido,
  };
}

/** El descuento en pesos que resulta de `d` sobre `base` (pesos), como lo calcula el servidor. */
export function montoDeDescuento(d, base) {
  return pesos(descuentoEnCentavos(d, aCentavos(base)));
}

/** Cambio exacto: efectivo − total, en pesos con dos decimales. */
export function cambioDe(efectivo, total) {
  return pesos(aCentavos(efectivo) - aCentavos(total));
}
